import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { motion } from "framer-motion";
import "swiper/css";

import {
  Phone,
  PenTool,
  Map,
  RefreshCw,
  Box,
  FileText,
  ClipboardCheck,
} from "lucide-react";

const Services = () => {
  // ✅ Define steps here
  const steps = [
    {
      icon: Phone,
      title: "Initial Consultation",
      desc: "We begin with a detailed conversation to understand your goals, budget, and vision.",
    },
    {
      icon: PenTool,
      title: "Creative Design",
      desc: "Our expert team creates unique and functional design concepts tailored to your needs.",
    },
    {
      icon: Map,
      title: "Planning & Strategy",
      desc: "We prepare detailed plans, layouts, and timelines to ensure clarity and precision.",
    },
    {
      icon: RefreshCw,
      title: "Execution & Delivery",
      desc: "From groundbreaking to handover, we execute with excellence and deliver on time.",
    },
    {
      icon: FileText,
      title: "Quality Assurance",
      desc: "Every project undergoes strict quality checks to meet the highest standards.",
    },
    {
      icon: ClipboardCheck,
      title: "Final Handover",
      desc: "We ensure smooth project completion with transparent reporting and client satisfaction.",
    },
  ];

  const servicesData = [
    {
      id: "01",
      title: "Innovative Architecture that Inspires Living",
      desc: "As the best architect in Varanasi, our team excels in crafting innovative and functional designs tailored to your needs. From residential to commercial projects, we provide custom solutions that reflect your vision.",
      subServices: [
        "Residence Architecture",
        "Commercial Architecture",
        "Renovation & Redesign",
        "Hospital Architecture",
        "Hotel & Resort Architecture",
        "School & Colleges",
      ],
      images: [
        "/images/12.jpeg",
        "/images/6.jpeg",
        "/images/21.jpeg",
        "/images/22.jpeg",
       ],
    },
    {
      id: "02",
      title: "Interior Designs that Reflect Your Lifestyle",
      desc: "Our team, recognized as the best interior designer in Varanasi, creates stylish and functional interiors that harmonize with your lifestyle. We ensure a perfect balance of aesthetics and practicality while keeping within budget.",
      subServices: [
        "Flat Interior",
        "Home Interior",
        "Bedroom Interior",
        "Hotel Interior",
        "Kitchen Interior",
        "Shop Interior",
      ],
      images: [
       "/images/29.jpeg",
        "/images/28.jpeg",
        "/images/27.jpeg",
        "/images/26.jpeg",
      ],
    },
    {
      id: "03",
      title: "Smart & Sustainable Construction Solutions",
      desc: "We provide end-to-end construction services with modern techniques and high-quality materials. Our projects are executed with precision, ensuring durability and sustainability.",
      subServices: [
        "Residential Construction",
        "Commercial Construction",
        "Turnkey Projects",
        "Renovation",
      ],
      images: [
       "/images/17.jpeg",
        "/images/18.jpeg",
        "/images/20.jpeg",
        "/images/25.jpeg",
      ],
    },
    {
      id: "04",
      title: "Transforming Outdoors into Green Paradises",
      desc: "Transform your outdoor spaces into serene and aesthetic environments with our landscaping services. We create gardens, lawns, and green spaces that complement your property.",
      subServices: [
        "Garden Design",
        "Lawn Setup",
        "Outdoor Seating",
        "Water Features",
      ],
      images: [
       "/images/21.jpeg",
        "/images/22.jpeg",
        "/images/Braj-4.jpeg",
        "/images/Braj-5.jpeg",
      ],
    },
  ];

  return (
    <section className="w-full bg-white text-gray-800">
      {/* Breadcrumb Section */}
      <div
  className="relative w-full h-64 bg-cover bg-center bg-[url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=80')]"
>
        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-4xl md:text-5xl font-bold text-yellow-400 drop-shadow-md"
          >
            Services
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-2 text-white font-medium"
          >
            Home &gt; <span className="text-yellow-400">Services</span>
          </motion.p>
        </div>
      </div>

      {/* Service Intro */}
      <div className="max-w-7xl mx-auto py-12 px-6 text-center">
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-lg uppercase tracking-wide text-yellow-600 font-semibold"
        >
          Services We Offer
        </motion.h3>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          viewport={{ once: true }}
          className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2"
        >
          Crafting Spaces, Building Dreams – Discover Our Signature Services
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-4 text-gray-600 max-w-3xl mx-auto"
        >
          From innovative architectural designs to detailed project management,
          we offer a full suite of services to bring your vision to life. Our
          expert team ensures every project is executed with precision and
          creativity.
        </motion.p>
      </div>

      {/* Dynamic Sections */}
      {servicesData.map((service, index) => (
        <div
          key={service.id}
          className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 px-6 py-14 items-center"
        >
          {index % 2 === 0 ? (
            <>
              {/* Slider */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
              >
                <Swiper
                  loop
                  spaceBetween={20}
                  slidesPerView={1}
                  autoplay={{ delay: 2500, disableOnInteraction: false }}
                  modules={[Autoplay]}
                  className="rounded-xl overflow-hidden shadow-2xl"
                >
                  {service.images.map((img, idx) => (
                    <SwiperSlide key={idx}>
                      <img
                        src={img}
                        alt={`${service.title} ${idx + 1}`}
                        className="w-full h-[400px] object-cover"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </motion.div>

              {/* Text */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-gray-900">
                  <span className="text-yellow-600 mr-2">{service.id}.</span>{" "}
                  {service.title}
                </h3>
                <p className="mt-4 text-gray-600">{service.desc}</p>
                <div className="grid grid-cols-2 gap-3 mt-6">
                  {service.subServices.map((s, idx) => (
                    <button
                      key={idx}
                      className="px-4 py-2 border border-yellow-500 rounded-lg text-gray-700 hover:bg-yellow-500 hover:text-white transition shadow-sm"
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <a
                  href="https://wa.me/919876543210"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-6 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-md transition"
                >
                  WhatsApp Us
                </a>
              </motion.div>
            </>
          ) : (
            <>
              {/* Text */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-gray-900">
                  <span className="text-yellow-600 mr-2">{service.id}.</span>{" "}
                  {service.title}
                </h3>
                <p className="mt-4 text-gray-600">{service.desc}</p>
                <div className="grid grid-cols-2 gap-3 mt-6">
                  {service.subServices.map((s, idx) => (
                    <button
                      key={idx}
                      className="px-4 py-2 border border-yellow-500 rounded-lg text-gray-700 hover:bg-yellow-500 hover:text-white transition shadow-sm"
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <a
                  href="https://wa.me/919876543210"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-6 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-md transition"
                >
                  WhatsApp Us
                </a>
              </motion.div>

              {/* Slider */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
              >
                <Swiper
                  loop
                  spaceBetween={20}
                  slidesPerView={1}
                  autoplay={{ delay: 2500, disableOnInteraction: false }}
                  modules={[Autoplay]}
                  className="rounded-xl overflow-hidden shadow-2xl"
                >
                  {service.images.map((img, idx) => (
                    <SwiperSlide key={idx}>
                      <img
                        src={img}
                        alt={`${service.title} ${idx + 1}`}
                        className="w-full h-[400px] object-cover"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </motion.div>
            </>
          )}
        </div>
      ))}

      {/* Process Steps Section */}
      <section className="relative py-20 bg-gradient-to-br from-yellow-50 via-white to-yellow-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              Our <span className="text-yellow-600">Process</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We follow a structured yet flexible approach to turn your ideas
              into reality. Each step is designed to ensure clarity, creativity,
              and precision.
            </p>
          </motion.div>

          {/* Steps Timeline/Grid */}
          <div className="relative">
            {/* vertical line in center (timeline effect) */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 h-full w-1 bg-yellow-200 hidden lg:block"></div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 relative">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className={`relative flex items-start gap-6 p-6 bg-white rounded-2xl shadow-lg border border-gray-200 hover:shadow-2xl transition-all duration-300 group
                    ${index % 2 === 0 ? "lg:pr-16 lg:ml-auto" : "lg:pl-16 lg:mr-auto"}`}
                >
                  {/* Connector dot */}
                  <div
                    className={`absolute hidden lg:block top-8 w-6 h-6 rounded-full border-4 border-white bg-yellow-500 shadow-md
                      ${index % 2 === 0 ? "-right-3" : "-left-3"}`}
                  ></div>

                  {/* Icon with step number */}
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-yellow-400 to-yellow-600 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition">
                      <step.icon size={28} />
                    </div>
                    <span className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 text-xs font-semibold bg-gray-900 text-white px-2 py-0.5 rounded-full shadow">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>

                  {/* Text Content */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-yellow-600 transition">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </section>
  );
};

export default Services;
